/**
 * Dear Diary Theme Showcase
 * Demonstrates all Phase 3 & 4 decorative elements and personalization
 */

import React from 'react'
import { Link } from 'react-router-dom'
import { useTheme } from '@/components/providers/ThemeProvider'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import {
  PaperClip,
  WashiTape,
  CoffeeStain,
  Stamp,
  BookmarkRibbon,
  DoodleStars,
  DateStamp,
  TapeCorners,
  DrawnCheckbox,
  PageNumber,
  // Phase 4 components
  JournalCover,
  HortonAvatar,
  HortonWithBubble,
  HortonGreeting,
  // Hierarchy components
  HierarchyCard,
  HierarchyListItem,
  HierarchyBreadcrumb,
} from '@/components/diary'
import {
  HortonNote,
  StickyNote,
  Annotation,
  DiaryDivider,
  YanamasFlyleaf,
  PersonDossier,
  PlacePostcard,
} from '@/components/diary'

export default function DiaryShowcase() {
  const { isDiary, setThemeStyle } = useTheme()
  const [checkboxes, setCheckboxes] = React.useState({
    item1: false,
    item2: true,
    item3: false,
  })

  if (!isDiary) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>📖 Dear Diary Showcase</CardTitle>
            <CardDescription>
              This page showcases the Dear Diary theme's decorative elements and micro-interactions.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              Please enable the Dear Diary theme to see all the decorations.
            </p>
            <Button onClick={() => setThemeStyle('diary')}>
              🎨 Enable Dear Diary Theme
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen p-8 max-w-6xl mx-auto relative">
      {/* Page Number */}
      <PageNumber number="42" position="bottom-center" />
      
      {/* Header */}
      <div className="text-center mb-12">
        <h1 
          className="text-4xl mb-4"
          style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-sepia)' }}
        >
          ✨ Dear Diary Showcase ✨
        </h1>
        <DateStamp format="long" />
        <DoodleStars count={5} spread="wide" className="mt-4 justify-center" />
      </div>

      {/* Decorative Elements Section */}
      <section className="mb-12">
        <h2 
          className="text-2xl mb-6 diary-underline-hand"
          style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-sepia)' }}
        >
          📎 Decorative Elements
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Card with Paper Clip */}
          <Card variant="journal" className="relative">
            <PaperClip color="silver" position="top-right" />
            <CardHeader>
              <CardTitle>Paper Clips</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ fontFamily: "var(--font-body, 'Lora', serif)" }}>
                Add paper clips in silver, gold, rose, or black. Position them on any corner!
              </p>
            </CardContent>
          </Card>

          {/* Card with Washi Tape */}
          <Card variant="journal" className="relative overflow-visible">
            <WashiTape color="pink" width={100} rotation={-5} position={{ top: '-12px', left: '10px' }} />
            <WashiTape color="blue" width={80} rotation={3} position={{ top: '-12px', right: '20px' }} />
            <CardHeader className="pt-8">
              <CardTitle>Washi Tape</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ fontFamily: "var(--font-body, 'Lora', serif)" }}>
                Colorful washi tape strips with patterns. Pink, blue, yellow, green, or purple!
              </p>
            </CardContent>
          </Card>

          {/* Card with Coffee Stain */}
          <Card variant="note" className="relative">
            <CoffeeStain size="medium" opacity={0.1} position={{ bottom: '10px', right: '10px' }} />
            <CardHeader>
              <CardTitle>Coffee Stains</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ fontFamily: "var(--font-body, 'Lora', serif)" }}>
                Subtle coffee ring marks add authenticity. Small, medium, or large sizes.
              </p>
            </CardContent>
          </Card>

          {/* Card with Bookmark Ribbon */}
          <Card variant="journal" className="relative overflow-visible">
            <BookmarkRibbon color="red" position="right" length={90} />
            <CardHeader>
              <CardTitle>Bookmark Ribbons</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ fontFamily: "var(--font-body, 'Lora', serif)" }}>
                Elegant ribbon bookmarks in red, blue, gold, or green.
              </p>
            </CardContent>
          </Card>

          {/* Stamps */}
          <Card variant="journal">
            <CardHeader>
              <CardTitle>Stamps</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-4">
                <Stamp text="Approved" color="purple" />
                <Stamp text="Important" color="red" rotation={5} />
                <Stamp text="Done" color="green" rotation={-3} />
              </div>
            </CardContent>
          </Card>

          {/* Tape Corners */}
          <Card variant="journal">
            <CardHeader>
              <CardTitle>Photo Corners</CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center">
              <TapeCorners color="yellow">
                <div 
                  className="w-32 h-24 flex items-center justify-center text-4xl"
                  style={{ background: 'var(--paper-margin)' }}
                >
                  📷
                </div>
              </TapeCorners>
            </CardContent>
          </Card>
        </div>
      </section>

      <DiaryDivider variant="flourish" />

      {/* Margin Notes Section */}
      <section className="mb-12 relative">
        <h2 
          className="text-2xl mb-6 diary-underline-hand"
          style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-sepia)' }}
        >
          📝 Margin Notes & Annotations
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* yanamas Notes */}
          <div className="space-y-6">
            <h3 style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-faded)' }}>yanamas' Messages</h3>
            
            <HortonNote mood="happy">
              Hey there! I'm proud of the progress you're making. Keep it up! 🎉
            </HortonNote>
            
            <HortonNote variant="sticky" mood="encouraging">
              Remember: Every small step counts. You've got this!
            </HortonNote>
          </div>

          {/* Sticky Notes */}
          <div className="space-y-6">
            <h3 style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-faded)' }}>Sticky Notes</h3>
            
            <div className="flex flex-wrap gap-4">
              <StickyNote color="yellow" rotation={-3}>
                Don't forget to take breaks! ☕
              </StickyNote>
              
              <StickyNote color="pink" rotation={2} pinned={false}>
                Meeting at 3pm tomorrow
              </StickyNote>
              
              <StickyNote color="blue" rotation={-1}>
                Call mom 💕
              </StickyNote>
            </div>
          </div>
        </div>
      </section>

      <DiaryDivider variant="dots" />

      {/* Text Annotations Section */}
      <section className="mb-12">
        <h2 
          className="text-2xl mb-6 diary-underline-hand"
          style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-sepia)' }}
        >
          ✍️ Text Annotations
        </h2>
        
        <Card variant="journal">
          <CardContent className="pt-6">
            <p style={{ fontFamily: "var(--font-body, 'Lora', serif)", fontSize: '1.1rem', lineHeight: 1.8 }}>
              You can <Annotation type="highlight" color="yellow">highlight important text</Annotation> or{' '}
              <Annotation type="underline" color="pink">underline key points</Annotation>. 
              Mark things as <Annotation type="crossout" color="pink">no longer relevant</Annotation> or{' '}
              <Annotation type="circle" color="green" note="This is really important!">circle emphasis</Annotation>.
            </p>
          </CardContent>
        </Card>
      </section>

      <DiaryDivider variant="doodle" />

      {/* Hand-drawn Checkboxes */}
      <section className="mb-12">
        <h2 
          className="text-2xl mb-6 diary-underline-hand"
          style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-sepia)' }}
        >
          ☑️ Hand-drawn Checkboxes
        </h2>
        
        <Card variant="journal">
          <CardContent className="pt-6 space-y-4">
            <DrawnCheckbox 
              checked={checkboxes.item1} 
              onChange={(v) => setCheckboxes(prev => ({ ...prev, item1: v }))}
              label="Learn something new today"
            />
            <DrawnCheckbox 
              checked={checkboxes.item2} 
              onChange={(v) => setCheckboxes(prev => ({ ...prev, item2: v }))}
              label="Practice gratitude"
            />
            <DrawnCheckbox 
              checked={checkboxes.item3} 
              onChange={(v) => setCheckboxes(prev => ({ ...prev, item3: v }))}
              label="Take a mindful break"
            />
          </CardContent>
        </Card>
      </section>

      <DiaryDivider variant="line" />

      {/* Micro-interactions */}
      <section className="mb-12">
        <h2 
          className="text-2xl mb-6 diary-underline-hand"
          style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-sepia)' }}
        >
          ✨ Micro-interactions
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Card variant="journal">
            <CardHeader>
              <CardTitle>Hover Lift Effect</CardTitle>
              <CardDescription>Cards lift slightly when you hover over them!</CardDescription>
            </CardHeader>
            <CardContent>
              <p style={{ fontFamily: "var(--font-body, 'Lora', serif)" }}>
                Try hovering over any card to see the paper lift animation.
              </p>
            </CardContent>
          </Card>

          <Card variant="journal">
            <CardHeader>
              <CardTitle>Button Animations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-3">
                <Button>Stamp Press</Button>
                <Button variant="secondary">Washi Tape</Button>
                <Button variant="outline">Pen Border</Button>
              </div>
              <p style={{ fontFamily: "var(--font-body, 'Lora', serif)", fontSize: '0.9rem', color: 'var(--ink-faded)' }}>
                Click buttons to see the ink ripple effect!
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <DiaryDivider variant="flourish" />

      {/* Hierarchy Cards */}
      <section className="mb-12">
        <h2 
          className="text-2xl mb-6 diary-underline-hand"
          style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-sepia)' }}
        >
          📚 Hierarchy Cards with Washi Tape
        </h2>
        
        {/* Breadcrumb example */}
        <div className="mb-6">
          <HierarchyBreadcrumb 
            path={[
              { level: 'dream', title: 'Learn Piano' },
              { level: 'aspiration', title: 'Master Basics' },
              { level: 'goal', title: 'Complete Book 1' },
            ]}
          />
        </div>
        
        {/* Hierarchy cards - note the different sizes and colors */}
        <div className="space-y-4">
          <HierarchyCard 
            level="dream" 
            title="Learn to Play Piano" 
            description="Master piano and play my favorite songs"
          >
            <p style={{ fontFamily: "var(--font-body, 'Lora', serif)" }}>
              A lifelong dream to create beautiful music...
            </p>
          </HierarchyCard>
          
          <HierarchyCard 
            level="aspiration" 
            title="Master Piano Basics" 
            description="Build a strong foundation"
          />
          
          <HierarchyCard 
            level="goal" 
            title="Complete Piano Book 1" 
            description="Finish all exercises in beginner book"
          />
          
          <HierarchyCard 
            level="task" 
            title="Practice scales daily" 
            description="20 minutes of C major scale"
          />
          
          <HierarchyCard 
            level="action-item" 
            title="Practice right hand C scale" 
          />
        </div>

        {/* List items */}
        <h3 className="mt-8 mb-4" style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-faded)' }}>
          Compact List View
        </h3>
        <div className="space-y-2">
          <HierarchyListItem level="dream" title="Learn Piano" subtitle="In Progress" status="in-progress" />
          <HierarchyListItem level="goal" title="Complete Book 1" subtitle="3 of 10 chapters" status="in-progress" />
          <HierarchyListItem level="task" title="Practice scales" subtitle="Daily" status="completed" />
          <HierarchyListItem level="action-item" title="Right hand practice" status="pending" />
        </div>
      </section>

      <DiaryDivider variant="dots" />

      {/* Phase 4: Personalization */}
      <section className="mb-12">
        <h2 
          className="text-2xl mb-6 diary-underline-hand"
          style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-sepia)' }}
        >
          🎭 yanamas' Moods (Phase 4)
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <div className="text-center">
            <HortonAvatar mood="happy" size="lg" style="sticker" />
            <p className="mt-2 text-sm" style={{ color: 'var(--ink-faded)' }}>Happy</p>
          </div>
          <div className="text-center">
            <HortonAvatar mood="encouraging" size="lg" style="sticker" />
            <p className="mt-2 text-sm" style={{ color: 'var(--ink-faded)' }}>Encouraging</p>
          </div>
          <div className="text-center">
            <HortonAvatar mood="thinking" size="lg" style="sticker" />
            <p className="mt-2 text-sm" style={{ color: 'var(--ink-faded)' }}>Thinking</p>
          </div>
          <div className="text-center">
            <HortonAvatar mood="celebrating" size="lg" style="sticker" />
            <p className="mt-2 text-sm" style={{ color: 'var(--ink-faded)' }}>Celebrating</p>
          </div>
        </div>

        <div className="space-y-6">
          <h3 style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-faded)' }}>yanamas with Messages</h3>
          
          <HortonWithBubble 
            mood="encouraging" 
            size="md" 
            style="sticker" 
            message="You're making great progress! Keep going!" 
          />
          
          <HortonGreeting />
        </div>
      </section>

      <DiaryDivider variant="dots" />

      {/* Journal Cover Preview */}
      <section className="mb-12">
        <h2 
          className="text-2xl mb-6 diary-underline-hand"
          style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-sepia)' }}
        >
          📔 Journal Cover (Phase 4)
        </h2>
        
        <p className="mb-6" style={{ fontFamily: "var(--font-body, 'Lora', serif)", color: 'var(--ink-faded)' }}>
          Click the edit button to personalize your journal cover!
        </p>
        
        <JournalCover />
      </section>

      <DiaryDivider variant="dots" />

      {/* Yanamas theme surfaces */}
      <section className="mb-12">
        <h2
          className="text-2xl mb-6 diary-underline-hand"
          style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-sepia)' }}
        >
          🐘 Yanamas (previously horton.io)
        </h2>

        <p className="mb-6" style={{ fontFamily: "var(--font-body, 'Lora', serif)", color: 'var(--ink-faded)' }}>
          The diary theme's mission surfaces: the flyleaf, people as dossiers,
          and places as living postcards. On the real pages these carry your
          actual data.
        </p>

        <YanamasFlyleaf alwaysShow className="mb-8" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PersonDossier
            id="showcase-1"
            name="Maya Rivera"
            relation="my daughter"
            aliases={['May-May']}
            summary="Loves the science fair, currently obsessed with volcanoes."
            fields={[
              { label: 'school', value: 'Jefferson Elementary' },
              { label: 'grade', value: '4th grade' },
              { label: 'interests', value: 'volcanoes, swimming' },
            ]}
            tags={['family']}
            itemCount={3}
          />
          <PlacePostcard
            title="Riverside Park loop"
            address="400 Riverside Dr"
            tags={['running', 'morning']}
            latitude={40.8054}
            longitude={-73.9724}
            radiusM={150}
          />
        </div>

        <p className="mt-6 text-sm" style={{ fontFamily: "var(--font-body, 'Lora', serif)", color: 'var(--ink-faded)' }}>
          The Life Map's Storybook view lives on{' '}
          <Link to="/life-map" style={{ color: 'var(--link-default)' }}>the Life Map page</Link>{' '}
          — in diary mode it opens as chapters and handwritten entries.
        </p>
      </section>

      {/* Page Turn Info */}
      <Card variant="note" className="mt-8">
        <CardContent className="pt-6 text-center">
          <p style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", fontSize: '1.1rem', color: 'var(--ink-sepia)' }}>
            🎨 All these decorations only appear when the Dear Diary theme is active!
          </p>
          <p style={{ fontFamily: "var(--font-body, 'Lora', serif)", marginTop: '0.5rem', color: 'var(--ink-faded)' }}>
            Switch to Modern theme and they gracefully disappear.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
